import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { defineStore } from 'pinia'

import ApiService from '@dogfy/services/apiService'
import endpoints from '@dogfy/services/apiEndpoints'
import config from '@/config'
import bagFormats from '@dogfy/services/data/bagFormats'
import { getDistributedDogMenu } from '@dogfy/lib/utils'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrAfter)

export const useAccountStore = defineStore('account', () => {
  const route = useRoute()

  // State
  // ----------------------
  const user = ref(null)
  const dogs = ref([])
  const orders = ref(new Map())
  const subscription = ref(null)
  const engagementHistory = ref({
    testId: '',
    isEngaged: false,
  })

  // Getters
  // ----------------------
  const userCountry = computed(() => user.value?.country)
  const dogNames = computed(() => dogs.value.map(({ name }) => name))
  const hasMultipleDogs = computed(() => dogs.value.length > 1)

  const isTrial = computed(() => subscription.value?.status === 'trial')
  const selectedDog = computed(() => route.params.dogId ? dogs.value.find(dog => dog._id === route.params.dogId) : dogs.value[0])

  const isSubscriptionPaused = computed(() => subscription.value?.status === 'paused')
  const isSubscriptionTrial = computed(() => subscription.value?.status === 'trial')
  const isSubscriptionFailedPayment = computed(() => subscription.value?.status === 'failedPayment')
  const isSubscriptionPausedOrFailedPayment = computed(() => isSubscriptionPaused.value || isSubscriptionFailedPayment.value)

  const hasPaymentPendingPausedReason = computed(() => isSubscriptionPaused.value && subscription.value?.reasonForPause.code === 'payment-pending')

  const additionalCosts = computed(() => shippingAmount.value + (subscription.value?.extrasAmount || 0) + deliveryFeeAmount.value)

  const discountlessMonthlyAmount = computed(() => subscription.value?.amount + additionalCosts.value)

  const monthlyAmount = computed(() => {
    const monthlyAmount = ((1 - (subscription.value?.computedDiscountPercent || 0)) * (subscription.value?.amount || 0)) + additionalCosts.value
    return monthlyAmount < 0 ? 0 : monthlyAmount
  })

  const deliveryFeeAmount = computed(() => subscription.value?.additionalServices?.hasPaidAdditionalDelivery
    ? config.paidAdditionalDeliveryFee
    : 0)

  const canAddDog = computed(() => subscription.value?.computedDiscountPercent < 0.8) // At this moment, the condition to be able to add a dog is that the customer doesn't have a discount equal or over 80%.

  // FIXME: This variable should be just the price of a single delivery, and it should be multiplied by the ordersInCycle wherever it's used.
  const shippingAmount = computed(() => (config.deliveryCompanies[userCountry.value][subscription.value?.deliveryCompany]?.price) * subscription.value?.ordersInCycle)

  const ordersGrouped = computed(() => {
    // TODO: Uncomment this block when groupBy is supported in all iOS devices.
    //   return Map.groupBy(orders.value.values(), ({ deliveryDate, status }) => {
    //     if (dayjs().startOf('day').isAfter(dayjs(deliveryDate).startOf('day'))) return 'past'
    //     else return status !== 'cancelled' ? 'active' : 'futureNoActive'
    //   })

    if (!orders.value) return new Map()

    // This block is essential to maintain backward compatibility with older iOS versions.
    const groupedOrders = new Map([
      ['past', []],
      ['active', []],
      ['futureNoActive', []],
    ])

    const today = dayjs().startOf('day')

    for (const order of orders.value.values()) {
      const deliveryDate = dayjs(order.deliveryDate).startOf('day')

      if (today.isAfter(deliveryDate)) {
        groupedOrders.get('past').push(order)
      } else if (order.status !== 'cancelled') {
        groupedOrders.get('active').push(order)
      } else {
        groupedOrders.get('futureNoActive').push(order)
      }
    }

    return groupedOrders
  })

  const activeOrders = computed(() => ordersGrouped.value.get('active')?.sort((a, b) => new Date(a.deliveryDate).getTime() - new Date(b.deliveryDate).getTime()) || [])

  // Actions
  // ----------------------
  async function fetchAccountData () {
    try {
      await Promise.all([
        fetchOwnUserData(),
        fetchOwnOrders(),
      ])

      // Once the subscription is set, we can fetch the afilliates data.
      await fetchAffiliates()
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function fetchOwnUserData () {
    try {
      const { data } = await ApiService.get(endpoints.self._)
      const { dogs: userDogs, subscription: userSubscription, ...userData } = data
      user.value = userData
      subscription.value = userSubscription

      // It's done for those old customers that have never renewed subscription.
      if (!subscription.value.computedDiscountPercent) {
        subscription.value.computedDiscountPercent = 0
      }

      dogs.value = userDogs.map((dog) => ({
        ...dog,
        // We need to set these variables in order to show the recommendations. Remove them if the schema sets them in the API.
        hasRecommendedGrams: true,
        hasRecommendedMenus: true,
      }))
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function fetchOwnOrders () {
    try {
      const { data: { docs: orderList } } = await ApiService.get(endpoints.self.orders)
      orders.value = new Map(orderList.map((order) => [order._id, order]))
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function fetchAffiliates () {
    try {
      const { data } = await ApiService.get(endpoints.self.affiliates)
      subscription.value.affiliates = data
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function updateUserData (newData) {
    try {
      const { email, phone, prefix, givenName, familyName, country } = newData

      const { data } = await ApiService.patch(endpoints.self._, {
        email,
        phone,
        prefix,
        givenName,
        familyName,
        country,
      })

      user.value = data
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function updateAddressData (newData) {
    try {
      const { line1, locality, zip } = newData

      const { data } = await ApiService.patch(endpoints.self.address, {
        line1,
        locality,
        zip,
        country: user.value.country,
      })

      user.value = data
      await fetchOwnOrders()
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function updateSubscriptionExtras (extraType, newExtra) {
    try {
      const { data } = await ApiService.put(endpoints.self.subscription.extra(extraType)._, newExtra)

      subscription.value = data.subscription

      // It's done for those old customers that have never renewed subscription.
      if (!subscription.value.computedDiscountPercent) {
        subscription.value.computedDiscountPercent = 0
      }

      await Promise.all([
        fetchOwnOrders(),
        fetchAffiliates(),
      ])
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function updateSubscriptionStatus (newStatus, reason) {
    try {
      const { data } = await ApiService.patch(endpoints.self.subscription.status, { newStatus, reason })

      subscription.value = data.subscription

      // It's done for those old customers that have never renewed subscription.
      if (!subscription.value.computedDiscountPercent) {
        subscription.value.computedDiscountPercent = 0
      }

      await Promise.all([
        fetchOwnOrders(),
        fetchAffiliates(),
      ])
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  function isDeliveryDateEditable (order = null) {
    // Not a getter because we want the now() to be refreshed on each call.
    if (!order) {
      order = activeOrders.value[0]

      // Some customers may not have active orders (if their subscription is paused or cancelled, for instance).
      if (!order) return false
    }

    if (!['scheduled', 'paid'].includes(order.status) || isSubscriptionFailedPayment.value || isSubscriptionTrial.value) return false
    // Only allow edit order delivery date if it's an active "regular" order.
    if (activeOrders.value.filter(order => !order.isAdditional).findIndex(o => o._id === order._id) === -1) return false

    const now = dayjs()
    const paymentDeliveryDeltaDays = config.deliveryDeltaDays.payment[user.value?.country]
    const cutoff = dayjs(order.deliveryDate.substring(-10)).tz('Europe/Paris').startOf('day').subtract(paymentDeliveryDeltaDays, 'day').add(config.paymentsCutoffHour, 'hours')

    return (now < cutoff)
  }

  async function updateDogData (dog) {
    // Some dogs from previous clients might not have the activity level set.
    // Since this field is now essential for calculating the D.E.R, We default it to "normal" if it's missing.
    dog.activityLevel ||= 'normal'

    try {
      const { data } = await ApiService.patch(endpoints.self.dog(dog._id)._, dog)
      const { dogs: userDogs, subscription: userSubscription } = data

      // Store extra data for each dog.
      const recommendedNutritionPlan = dogs.value.reduce((acc, dog) => {
        acc[dog._id] = {
          // We need to set these variables in order to show the recommendations. Remove them if the schema sets them in the API.
          hasRecommendedGrams: dog.hasRecommendedGrams,
          hasRecommendedMenus: dog.hasRecommendedMenus,
        }
        return acc
      }, {})

      dogs.value = userDogs.map((dog) => {
        const extraData = recommendedNutritionPlan[dog._id] || {}

        return {
          ...dog,
          hasRecommendedGrams: !!extraData.hasRecommendedGrams,
          hasRecommendedMenus: !!extraData.hasRecommendedMenus,
        }
      })

      // Modifying the dog may incur a subscription update.
      subscription.value = userSubscription

      // It's done for those old customers that have never renewed subscription.
      if (!subscription.value.computedDiscountPercent) {
        subscription.value.computedDiscountPercent = 0
      }

      await Promise.all([
        fetchOwnOrders(),
        fetchAffiliates(),
      ])
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function updateDogDailyGrams (dailyGrams) {
    // If the daily grams are changed, we need to update the number of bags per menu accordingly.
    // Get the new bag count based on the new grams.
    const dailyBags = bagFormats[selectedDog.value.dailyGrams].dailyBags
    const dailyBagCount = Object.keys(dailyBags).reduce((a, b) => a + dailyBags[b], 0)
    const totalBagsInCycle = dailyBagCount * config.daysInStandardCycle

    const currentTotalServings = totalBagsInCycle / subscription.value.ordersInCycle

    const newDailyBags = bagFormats[dailyGrams].dailyBags
    const newDailyBagCount = Object.keys(newDailyBags).reduce((a, b) => a + newDailyBags[b], 0)
    const newTotalBagsInCycle = newDailyBagCount * config.daysInStandardCycle

    const newTotalServings = newTotalBagsInCycle / subscription.value.ordersInCycle

    let menu = selectedDog.value.order.menu
    const allowedMenus = Object.keys(menu)

    if (currentTotalServings !== newTotalServings) {
      menu = getDistributedDogMenu(menu, allowedMenus, newTotalServings, newDailyBagCount)
    }

    // Update the menu with the new bags proportionally to the customer's selection.
    const updatedDog = { ...selectedDog.value, dailyGrams, menu }

    await updateDogData(updatedDog)
  }

  async function updateOrderDeliveryDate (orderId, newDate) {
    try {
      const { data } = await ApiService.patch(endpoints.self.order(orderId).deliveryDate, { date: newDate })

      const order = orders.value.get(orderId)
      order.deliveryDate = data.deliveryDate
      // TODO: Think how to avoid fetch orders again.
      await fetchOwnOrders()
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function updatePlan (selectedPlan) {
    try {
      const { data } = await ApiService.patch(endpoints.self.subscription.plan, { plan: selectedPlan })
      const { dogs: userDogs, subscription: userSubscription, ...userData } = data

      user.value = userData
      dogs.value = userDogs
      subscription.value = userSubscription

      // It's done for those old customers that have never renewed subscription.
      if (!subscription.value.computedDiscountPercent) {
        subscription.value.computedDiscountPercent = 0
      }

      await Promise.all([
        fetchOwnUserData(),
        fetchOwnOrders(),
        fetchAffiliates(),
      ])
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function updateOrderAddress (orderId, newAddress) {
    try {
      const { line1, locality, zip } = newAddress

      const { data } = await ApiService.patch(endpoints.self.order(orderId).address, {
        line1,
        locality,
        zip,
        country: userCountry.value,
      })

      const order = orders.value.get(orderId)
      order.address = data.address
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function updateOrderAdditionalExtras (orderId, additionalExtras, extraId) {
    try {
      const { data } = await ApiService.put(endpoints.self.order(orderId).additionalExtras(extraId), additionalExtras)
      const order = orders.value.get(orderId)
      Object.assign(order, data)
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function updateOrdersInCycle (ordersInCycle) {
    try {
      const { data } = await ApiService.patch(endpoints.self.subscription.cycle, { ordersInCycle })
      const { dogs: userDogs, subscription: userSubscription, ...userData } = data
      user.value = userData
      subscription.value = userSubscription

      // It's done for those old customers that have never renewed subscription.
      if (!subscription.value.computedDiscountPercent) {
        subscription.value.computedDiscountPercent = 0
      }

      // Store extra data for each dog.
      const recommendedNutritionPlan = dogs.value.reduce((acc, dog) => {
        acc[dog._id] = {
          // We need to set these variables in order to show the recommendations. Remove them if the schema sets them in the API.
          hasRecommendedGrams: dog.hasRecommendedGrams,
          hasRecommendedMenus: dog.hasRecommendedMenus,
        }
        return acc
      }, {})

      dogs.value = userDogs.map((dog) => {
        const extraData = recommendedNutritionPlan[dog._id] || {}

        return {
          ...dog,
          hasRecommendedGrams: !!extraData.hasRecommendedGrams,
          hasRecommendedMenus: !!extraData.hasRecommendedMenus,
        }
      })

      // fetchOwnOrders is needed to update the active orders when the cycle is updated.
      await Promise.all([
        fetchOwnUserData(),
        fetchOwnOrders(),
        fetchAffiliates(),
      ])
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function updateDeliveryCompany (deliveryCompany) {
    try {
      const { data } = await ApiService.put(endpoints.self.subscription.deliveryCompany, { deliveryCompany })
      subscription.value = data.subscription

      // It's done for those old customers that have never renewed subscription.
      if (!subscription.value.computedDiscountPercent) {
        subscription.value.computedDiscountPercent = 0
      }

      await Promise.all([
        fetchOwnOrders(),
        fetchAffiliates(),
      ])
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function applyCoupon (coupon) {
    try {
      const { data } = await ApiService.post(endpoints.self.subscription.coupon, { coupon: coupon.trim().toUpperCase() })
      subscription.value = data.subscription

      // It's done for those old customers that have never renewed subscription.
      if (!subscription.value.computedDiscountPercent) {
        subscription.value.computedDiscountPercent = 0
      }

      await Promise.all([
        fetchOwnOrders(),
        fetchAffiliates(),
      ])
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  function getNextEditableOrder () {
    if (!activeOrders.value) return null

    const editableOrder = activeOrders.value?.find(order => {
    // Use same time as API.
      const serverTime = dayjs().utc()
      const cutoffTime = dayjs().tz('Europe/Paris').startOf('day').add(config.paymentsCutoffHour, 'hours')

      const isPastCutoffTime = serverTime > cutoffTime
      const fromDay = isPastCutoffTime ? serverTime.startOf('day').add(1, 'day') : serverTime.startOf('day')

      return order.payment.status === 'scheduled' &&
            dayjs(order.payment.date).isSameOrAfter(fromDay) &&
            !order.isAdditional &&
            !order.isSecondary
    })

    const paymentDeliveryDeltaDays = config.deliveryDeltaDays.payment[userCountry.value]

    if (editableOrder) {
      return {
        ...editableOrder,
        deliveryDate: dayjs(editableOrder.deliveryDate).toISOString(),
        lastEditableDate: dayjs(editableOrder.deliveryDate).subtract(paymentDeliveryDeltaDays, 'day').toISOString(),
      }
    }

    const { deliveryDate: lastCycleDeliveryDate } = Array.from(orders.value.values()).find(({ isLastInCycle }) => isLastInCycle) || {}

    const weeksToAdd = subscription.value.paymentCycleWeeks / subscription.value.ordersInCycle

    const deliveryDate = dayjs(lastCycleDeliveryDate).add(weeksToAdd, 'week')

    return {
      isForecasted: true,
      deliveryDate: deliveryDate.toISOString(),
      firstEditableDate: dayjs(lastCycleDeliveryDate).subtract(paymentDeliveryDeltaDays, 'day').toISOString(),
      lastEditableDate: deliveryDate.subtract(paymentDeliveryDeltaDays, 'day').toISOString(),
    }
  }

  async function fetchAvailableDeliveryDates ({ country, date, type }) {
    try {
      const { data } = await ApiService.get(endpoints.config.availableDeliveryDates, {
        country,
        date,
        type,
      })

      return data
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function updateDogAvatar (file, dogId) {
    try {
      const { data } = await ApiService.post(endpoints.self.dog(dogId).avatar, file)

      const idx = dogs.value.findIndex(dog => dog._id === dogId)
      if (idx >= 0) {
        dogs.value[idx].avatar = data
      }
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function deleteDogAvatar (dogId) {
    try {
      await ApiService.delete(endpoints.self.dog(dogId).avatar)

      const idx = dogs.value.findIndex(dog => dog._id === dogId)
      if (idx !== -1) {
        delete dogs.value[idx].avatar
      }
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function fetchEngagementHistory (testId) {
    engagementHistory.value.testId = testId

    try {
      const { data } = await ApiService.get(endpoints.self.engagementHistory)
      engagementHistory.value.isEngaged = !!data.cust.tests?.[testId]?.isEngaged
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  async function updateEngagementHistory ({ recommendationData, tests }) {
    try {
      await ApiService.post(endpoints.self.engagementHistory, { recommendationData, tests })
      // TODO: Create a state that stores the engagement histories.
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  function $reset () {
    user.value = null
    subscription.value = null
    dogs.value = []
    orders.value = new Map()
  }

  return {
    user,
    orders,
    subscription,
    engagementHistory,
    dogs,
    userCountry,
    isSubscriptionTrial,
    isSubscriptionPaused,
    isSubscriptionFailedPayment,
    isSubscriptionPausedOrFailedPayment,
    discountlessMonthlyAmount,
    monthlyAmount,
    shippingAmount,
    canAddDog,
    deliveryFeeAmount,
    ordersGrouped,
    dogNames,
    getNextEditableOrder,
    activeOrders,
    hasMultipleDogs,
    hasPaymentPendingPausedReason,
    isDeliveryDateEditable,
    fetchAccountData,
    updateUserData,
    updateAddressData,
    updateSubscriptionExtras,
    updateSubscriptionStatus,
    updateDogData,
    updateDogDailyGrams,
    updatePlan,
    isTrial,
    selectedDog,
    applyCoupon,
    additionalCosts,
    updateOrdersInCycle,
    updateDeliveryCompany,
    updateOrderDeliveryDate,
    updateOrderAddress,
    updateOrderAdditionalExtras,
    fetchAvailableDeliveryDates,
    updateDogAvatar,
    deleteDogAvatar,
    updateEngagementHistory,
    fetchEngagementHistory,
    $reset,
  }
})
